import { ReactNode } from 'react';

import { Stack } from '@mui/material';
import clsx from 'clsx';

import { PageSectionHeader, PageSectionHeaderProps } from './PageSectionHeader';
import { tokens } from '../../../theme/generated/tokens';
import { CssVarDefinitions } from '../../../theme/types';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';

const classNamesFactory = classNamesFor('PageSection');

const classNames = {
  root: classNamesFactory.root(),
  header: classNamesFactory.element('header'),
};

export type PageSectionProps = {
  /**
   * Title of the section, displayed in the header
   */
  title?: ReactNode;
  /**
   * Content to be displayed at the end of the section header
   */
  headerEndContent?: ReactNode;
  /**
   * Main section content
   */
  children?: ReactNode;
  testId?: string;
  /**
   * Properties, forwarded to the underlying components
   */
  slotProps?: {
    header?: Partial<PageSectionHeaderProps>;
  };
  /**
   * styling, applied to the root Stack wrapper.
   */
  sx?: SxProps;
  /**
   * CSS variables applied to the card root.
   * Use to provide CSS variables for descendants of the card.
   * @example cssVars={{'--someVariable': '20px'}}
   */
  cssVars?: CssVarDefinitions;
  className?: string;
};

/**
 * Page layout primitive with an optional header (title + trailing content slot) and a content area
 */
export const PageSection = ({
  title,
  headerEndContent,
  children,
  testId,
  slotProps,
  sx,
  cssVars,
  className,
}: PageSectionProps) => {
  const hasHeader = !!(title || headerEndContent || slotProps?.header);
  return (
    <Stack
      gap="12px"
      className={clsx(classNames.root, className)}
      data-testid={testId}
      sx={mergeSx({ color: tokens.theme.fg.structural.primary }, sx)}
      style={cssVars}
    >
      {hasHeader && (
        <PageSectionHeader
          title={title}
          endContent={headerEndContent}
          {...slotProps?.header}
        />
      )}
      {children}
    </Stack>
  );
};
